import { Plus } from 'lucide-react';

import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { MONTHS, WEEKDAYS } from '../constants';
import { coversDay, fromISO, todayISO, weekCells } from '../utils/dates';
import { EventChip } from './EventChip';
import type { CalendarEventItem } from '../types/calendar';

export interface WeekGridProps {
  /** Any day inside the week being drawn. */
  cursor: string;
  events: CalendarEventItem[];
  onSelectEvent: (item: CalendarEventItem) => void;
  /** Start a new entry on that day. Absent on a read-only calendar. */
  onAddOn?: (iso: string) => void;
}

/**
 * One week, a column to a day.
 *
 * The month grid answers "what is this month like"; this answers "what is on
 * this week", so the columns are tall enough to show every entry with its time
 * rather than three and a count.
 */
export function WeekGrid({ cursor, events, onSelectEvent, onAddOn }: WeekGridProps) {
  const cells = weekCells(cursor);
  const today = todayISO();

  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[46rem] grid-cols-7 rounded-2xl border border-line">
        {cells.map((iso) => {
          const date = fromISO(iso);
          const isToday = iso === today;
          const dayEvents = events.filter((item) => coversDay(item, iso));

          return (
            <div
              key={iso}
              className="group flex min-h-[18rem] flex-col border-l border-line first:border-l-0"
            >
              <div
                className={cn(
                  'flex items-center justify-between gap-1 border-b border-line px-2.5 py-2',
                  isToday ? 'bg-accent-soft' : 'bg-surface-2',
                )}
              >
                <div className="min-w-0">
                  <p className="truncate text-[11px] font-black uppercase tracking-wider text-ink-3">
                    {WEEKDAYS[date.getDay()]}
                  </p>
                  <p
                    className={cn(
                      'truncate text-sm font-black tabular-nums',
                      isToday ? 'text-accent' : 'text-ink',
                    )}
                  >
                    {date.getDate()} {MONTHS[date.getMonth()]?.slice(0, 3)}
                  </p>
                </div>

                {onAddOn && (
                  <button
                    type="button"
                    aria-label={`Add an entry on ${formatDate(iso)}`}
                    onClick={() => onAddOn(iso)}
                    className="grid h-6 w-6 shrink-0 cursor-pointer place-items-center rounded-md text-ink-3 opacity-0 transition-opacity hover:bg-accent-soft hover:text-accent focus-visible:opacity-100 group-hover:opacity-100"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden />
                  </button>
                )}
              </div>

              <div className="flex flex-1 flex-col gap-1.5 bg-surface p-2">
                {dayEvents.length === 0 ? (
                  <p className="m-auto text-center text-[11px] font-medium text-ink-3">—</p>
                ) : (
                  dayEvents.map((item) => (
                    <EventChip
                      key={item.uuid}
                      item={item}
                      layout="column"
                      onSelect={onSelectEvent}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
